/**
 * Waits for data to be loaded and returns a promise that resolves when the data is loaded.
 * If the data is not loaded after a timeout, the promise rejects with an error.
 * If the data is not loaded after the loading is complete, the promise rejects with an error.
 *
 * @param checkFn - A function that returns true if the data is loaded.
 * @param isLoading - A boolean indicating if the data is still loading.
 * @param timeoutMs - The timeout in milliseconds.
 * @param intervalMs - The interval in milliseconds between checks.
 * @returns A promise that resolves when the data is loaded.
 */
export const waitForData = async (
  checkFn: () => boolean,
  isLoading: boolean,
  timeoutMs = 10000,
  intervalMs = 100
): Promise<void> => {
  const startTime = Date.now();

  return new Promise((resolve, reject) => {
    const check = () => {
      if (checkFn()) {
        resolve();
      } else if (!isLoading) {
        // If we're not loading anymore and still don't have data, fail immediately
        reject(new Error('Data loading completed but required data is missing'));
      } else if (Date.now() - startTime > timeoutMs) {
        reject(new Error('Timeout waiting for data'));
      } else {
        setTimeout(check, intervalMs);
      }
    };

    check();
  });
};
